import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, X, CheckCheck, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared notification bell + dropdown panel used by BOTH the Amigos
 * dashboard and the Lone Star cross-brand bridge. Each usage passes its
 * own session token explicitly (never relies on global axios defaults),
 * so the two dashboards' sessions never bleed into one another.
 *
 * Real-time strategy: SSE (Server-Sent Events) for the live unread count
 * with a silent fallback to 20s polling if the stream errors out (e.g.
 * proxy buffering). Polling pauses while the browser tab is hidden.
 */
const NotificationBell = ({ token }) => {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const panelRef = useRef(null);
  const esRef = useRef(null);
  const pollRef = useRef(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, authHeader);
      setCount(res.data.count || 0);
    } catch { /* silent — never disrupt the dashboard for a notification hiccup */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=15`, authHeader);
      setItems(res.data || []);
    } catch { setItems([]); }
    finally { setLoadingList(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchCount();

    let es;
    try {
      es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (e) => {
        try { setCount(JSON.parse(e.data).count || 0); } catch { /* ignore malformed frame */ }
      };
      es.onerror = () => { es.close(); esRef.current = null; startPolling(); };
      esRef.current = es;
    } catch {
      startPolling();
    }

    function startPolling() {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') fetchCount();
      }, 20000);
    }

    return () => {
      if (esRef.current) esRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    const onClickAway = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const markViewed = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/view`, {}, authHeader);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, viewed_at: new Date().toISOString() } : n)));
      fetchCount();
    } catch { /* non-critical */ }
  };

  const acknowledge = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/acknowledge`, {}, authHeader);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, acknowledged_at: new Date().toISOString(), viewed_at: n.viewed_at || new Date().toISOString() } : n)));
      fetchCount();
    } catch { /* non-critical */ }
  };

  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/dismiss`, {}, authHeader);
      setItems((prev) => prev.filter((n) => n.id !== id));
      fetchCount();
    } catch { /* non-critical */ }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      <button
        className="dash-btn"
        style={{ position: 'relative', padding: '0.55rem 0.7rem' }}
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            data-testid="notification-unread-badge"
            style={{
              position: 'absolute', top: -5, right: -5, background: 'var(--red-bright)', color: '#fff',
              fontSize: '0.65rem', fontWeight: 700, borderRadius: '99px', minWidth: 17, height: 17,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
              boxShadow: '0 0 0 2px var(--charcoal)',
            }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          style={{
            position: 'absolute', top: '110%', right: 0, width: 360, maxHeight: 440, overflowY: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border-brass)', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)', zIndex: 300,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.8rem 1rem', borderBottom: '1px solid var(--border-dim)' }}>
            <span style={{ fontFamily: 'var(--font-alt)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-primary)' }}>Notifications</span>
            {count > 0 && <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{count} unread</span>}
          </div>

          {loadingList ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem 1rem' }}>
              <Bell size={26} />
              <div style={{ fontSize: '0.85rem' }}>No notifications yet</div>
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                style={{
                  padding: '0.8rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: n.viewed_at ? 'transparent' : 'rgba(201,162,39,0.05)',
                }}
              >
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <span className={`priority-dot ${n.priority === 'urgent' ? 'urgent' : 'normal'}`} style={{ marginTop: 5 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{n.title}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{n.summary}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.6rem' }}>
                  {!n.viewed_at && (
                    <button onClick={() => markViewed(n.id)} className="dash-btn" style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem' }} data-testid={`notification-view-btn-${n.id}`}>
                      <Check size={11} /> Mark Viewed
                    </button>
                  )}
                  {!n.acknowledged_at && (
                    <button onClick={() => acknowledge(n.id)} className="dash-btn" style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem' }} data-testid={`notification-ack-btn-${n.id}`}>
                      <CheckCheck size={11} /> Acknowledge
                    </button>
                  )}
                  <button onClick={() => dismiss(n.id)} className="dash-btn" style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem', marginLeft: 'auto' }} data-testid={`notification-dismiss-btn-${n.id}`}>
                    <X size={11} />
                  </button>
                </div>
              </div>
            ))
          )}

          {items.length > 0 && (
            <div style={{ padding: '0.6rem 1rem', fontSize: '0.72rem', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <ExternalLink size={11} /> Viewing does not change lead/submission status.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
